import React, { useState } from 'react';
import { Slide, toast } from 'react-toastify';
import {
  Route,
  Switch,
  useHistory,
  useParams,
  useRouteMatch,
} from 'react-router-dom';

import { ReactComponent as Alert } from '@/assets/illustrations/feedback-graphics/alert.svg';
import ProtocoloExecutadoDetail from '@/features/execucao/components/ProtocoloExecutadoDetail';
import { useExecucaoContext } from '@/features/execucao/providers/ExecucaoProvider';
import ExecucaoService from '@/features/execucao/services/ExecucaoAPI';

import LoadingSwitchTransition from '@/components/LoadingSwitchTransition';
import PlaceholderLoading from '@/components/PlaceholderLoading';

import { closeMedflowApp } from '@/utils/window';
import ToastContent from '@/components/ToastContent';
import EndingTransitionLoading from '@/components/EndingTransitionLoading';

import { useQuery } from '@/utils/route';
import EnterpriseHeaderProtocoloExecutado from '../../components/EnterpriseHeaderProtocoloExecutado';
import { useEnterpriseContext } from '../../contexts/EnterpriseContext';
import EnterprisePageLayout from '../../layouts/EnterprisePageLayout';
import { ErrorContainer } from './styles';
import SumarioProtocoloEnterprise from '../SumarioProtocoloEnterprise';

const PlaceholderContent = () => (
  <PlaceholderLoading.Rect
    height={513}
    width="100%"
    style={{
      display: 'flex',
      borderRadius: 16,
    }}
  />
);

const PlaceholderTransition: React.FC<React.PropsWithChildren> = ({
  children,
}) => {
  const [{ protocoloExecutado }] = useExecucaoContext();

  return (
    <LoadingSwitchTransition
      loading={!protocoloExecutado}
      LoadingComponent={<PlaceholderContent />}
      style={{
        flex: 1,
      }}
    >
      {children}
    </LoadingSwitchTransition>
  );
};

const ExecucaoProocoloEnterprise: React.FC = () => {
  const history = useHistory();
  const query = useQuery();
  const { path } = useRouteMatch();
  const { id: protocoloId, session_id: sessionId } = useParams<{
    id: string;
    session_id?: string;
  }>();

  const [, { dispatchStartFromExecution }] = useEnterpriseContext();

  const [isLoadingProtocolo, setIsLoadingProtocolo] = useState(true);
  const [isError, setIsError] = useState(false);
  const [isFinalizado, setIsFinalizado] = useState(false);

  const [
    { protocoloExecutado },
    { dispatchSetExecucao },
  ] = useExecucaoContext();

  const disableNavigation = React.useMemo(() => {
    return query.get('disable_navigation') === 'true';
  }, [query]);

  const redirectToResumo = React.useCallback(() => {
    if (protocoloId) {
      toast.dark(
        () => (
          <ToastContent>
            <p>
              Este protocolo já foi finalizado. Você está visualizando a tela de
              resumo com as informações finais.
            </p>
          </ToastContent>
        ),
        {
          position: 'bottom-left',
          autoClose: 5000,
          transition: Slide,
        },
      );
      history.replace(`/enterprise/protocolos/${protocoloId}/resumo`);
    }
  }, [history, protocoloId]);

  React.useEffect(() => {
    if (protocoloExecutado) {
      setIsLoadingProtocolo(false);
    }
  }, [protocoloExecutado]);

  React.useEffect(() => {
    if (
      !isLoadingProtocolo &&
      protocoloExecutado &&
      protocoloExecutado.executado &&
      protocoloExecutado.status === 'FINALIZADO' &&
      !protocoloExecutado.passo_corrente
    ) {
      setIsFinalizado(true);
    }
  }, [isLoadingProtocolo, protocoloExecutado]);

  React.useEffect(() => {
    if (
      !isLoadingProtocolo &&
      protocoloExecutado &&
      protocoloExecutado.executado &&
      protocoloExecutado.passo_corrente
    ) {
      redirectToResumo();
    }
  }, [redirectToResumo, isLoadingProtocolo, protocoloExecutado]);

  React.useEffect(() => {
    if (!isLoadingProtocolo && !protocoloExecutado) {
      setIsError(true);
    }
  }, [isLoadingProtocolo, protocoloExecutado]);

  React.useEffect(() => {
    async function loadProtocolo() {
      try {
        const execucao = await ExecucaoService.getExecucao(
          protocoloId,
          {
            omit: ['passo_corrente'],
            expand: ['passos'],
          },
          sessionId,
        );
        dispatchSetExecucao(execucao);
        dispatchStartFromExecution(execucao);
      } catch {
        setIsError(true);
      }
    }

    loadProtocolo();
    // eslint-disable-next-line
  }, []);

  if (isError) {
    return (
      <ErrorContainer>
        <Alert />

        <h3>Ops, algo deu errado.</h3>

        <p>Por favor, tente novamente em alguns instantes.</p>
      </ErrorContainer>
    );
  }

  if (isFinalizado) {
    return (
      <EndingTransitionLoading
        content={{
          title: 'Protocolo finalizado',
          description:
            'Estamos retornando os dados para o seu prontuário. O Medflow fecha em instantes.',
        }}
        progressStep={36}
        intervalMs={1000}
        animationMs={800}
        endingTimeoutMs={800}
        onDone={() => {
          if (protocoloExecutado) closeMedflowApp(protocoloExecutado);
        }}
        promise={new Promise(resolve => setTimeout(resolve, 3000))}
      />
    );
  }

  if (disableNavigation) {
    return (
      <PlaceholderTransition>
        <Route exact path={`${path}`} component={ProtocoloExecutadoDetail} />
      </PlaceholderTransition>
    );
  }

  return (
    <EnterprisePageLayout Nav={EnterpriseHeaderProtocoloExecutado}>
      <PlaceholderTransition>
        <Switch>
          <Route exact path={`${path}`} component={ProtocoloExecutadoDetail} />
          <Route
            path={`${path}/sumario`}
            component={SumarioProtocoloEnterprise}
            exact
          />
        </Switch>
      </PlaceholderTransition>
    </EnterprisePageLayout>
  );
};

export default ExecucaoProocoloEnterprise;
